import { useState, useEffect } from "react";
import Header from "../Components/Header/Header";
import Parser from 'html-react-parser';
import Carousel from "../Components/Carousel/Carousel";
import StrapiBlocksRenderer from "../Components/StrapiBlocksRenderer/StrapiBlocksRenderer";
import Footer from "../Components/Footer/Footer";
import FinishScreen from "./FinishScreen";
import { useAuthContext } from "../context/AuthContext";
import { API } from "../constant";
import { getToken } from "../helpers";

const Quiz = (props) => {
    const [start, setStart] = useState(0);
    const [showFinish, setShowFinish] = useState(false);
    const [answers, setAnswers] = useState({});
    const [isFavorited, setIsFavorited] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);

    const { user } = useAuthContext();
    const introScreenInfo = props.Content;
    const firstScreen = props.Content.dc[0];
    const steps = props.Content.dc[0].question;

    // Handle starting the skill - scroll to top and push history state
    const handleStartSkill = () => {
        window.scrollTo(0, 0);
        window.history.pushState({ screen: 'skill' }, '');
        setStart(1);
    };

    // Handle browser back button
    useEffect(() => {
        const handlePopState = (event) => {
            if (start === 1 && !showFinish) {
                // Go back to intro screen
                setStart(0);
                window.scrollTo(0, 0);
            } else if (showFinish) {
                // Go back to carousel from finish
                setShowFinish(false);
                window.scrollTo(0, 0);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [start, showFinish]);

    // Scroll to top when screen changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [start, showFinish]);

    // Check if skill is already favorited
    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (!user || !props.SkillId) return;
            try {
                const response = await fetch(`${API}/users/${user.id}?populate=skills`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${getToken()}`,
                    }
                });
                const data = await response.json();
                const isAlreadyFavorited = data.skills?.some(skill => skill.id === props.SkillId);
                setIsFavorited(isAlreadyFavorited);
            } catch (error) {
                console.error("Error checking favorite status:", error);
            }
        };
        checkFavoriteStatus();
    }, [user, props.SkillId]);

    // Handle favorite toggle
    const handleFavorite = async () => {
        if (!user || !props.SkillId) return;
        setFavoriteLoading(true);
        try {
            const skillResponse = await fetch(`${API}/users/${user.id}?populate=skills`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                }
            });
            const responseSkillData = await skillResponse.json();
            let relatedSkills = responseSkillData.skills?.map(item => ({ id: item.id })) || [];

            const existingIndex = relatedSkills.findIndex(item => item.id === props.SkillId);
            if (existingIndex > -1) {
                relatedSkills.splice(existingIndex, 1);
                setIsFavorited(false);
            } else {
                relatedSkills.push({ id: props.SkillId });
                setIsFavorited(true);
            }

            await fetch(`${API}/users/${user.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ skills: relatedSkills }),
            });
        } catch (error) {
            console.error("Error updating favorite:", error);
        } finally {
            setFavoriteLoading(false);
        }
    };

    const handleAnswerClick = (questionIndex, selectedAnswer, correctAnswer) => {
        if (answers[questionIndex]) return;
        
        setAnswers(prev => ({
            ...prev,
            [questionIndex]: {
                selected: selectedAnswer,
                correct: correctAnswer,
                isCorrect: selectedAnswer === correctAnswer
            }
        }));
    };

    const getAnswerClass = (questionIndex, answerText, correctAnswer) => {
        const answer = answers[questionIndex];
        if (!answer) return '';
        
        if (answer.selected === answerText) {
            return answer.isCorrect ? 'correct' : 'incorrect';
        }
        
        if (!answer.isCorrect && answerText === correctAnswer) {
            return 'correct';
        }
        
        return '';
    };

    const getCorrectAnswerText = (item) => {
        if (!item?.correct_answer) return null;
        const key = item.correct_answer.replace('answer', 'answer_');
        return item[key] ?? item[item.correct_answer] ?? "Not found";
    };

    const carouselItems = steps.map((item, index) => {
        const answered = answers[index];
        const correctAnswerText = getCorrectAnswerText(item);
        const abbreviation = firstScreen.abbreviation?.[index];

        return (
            <div className="carousel-item" key={index}>
                <div className="carousel-item-inner">
                    <div className="item-content">
                        <h4>{abbreviation ? `${abbreviation.abbr} - ${abbreviation.title}` : props.Content.Title || ''}</h4>
                        <h3 className="carousel-question-title">{item.title}</h3>
                        <h2 className="carousel-item-title">{item.question}</h2>
                        <StrapiBlocksRenderer blocks={item.description} />
                        <div className="carousel-item-text">
                            <ul className="choose-option-type">
                                {item.answer_1 && (
                                    <li
                                        className={getAnswerClass(index, item.answer_1, correctAnswerText)}
                                        onClick={() => handleAnswerClick(index, item.answer_1, correctAnswerText)}
                                    >
                                        {item.answer_1}
                                    </li>
                                )}
                                {item.answer_2 && (
                                    <li
                                        className={getAnswerClass(index, item.answer_2, correctAnswerText)}
                                        onClick={() => handleAnswerClick(index, item.answer_2, correctAnswerText)}
                                    >
                                        {item.answer_2}
                                    </li>
                                )}
                                {item.answer_3 && (
                                    <li
                                        className={getAnswerClass(index, item.answer_3, correctAnswerText)}
                                        onClick={() => handleAnswerClick(index, item.answer_3, correctAnswerText)}
                                    >
                                        {item.answer_3}
                                    </li>
                                )}
                            </ul>
                        </div>
                        <div className={`quiz-feedback correct-answer ${answered ? 'show' : ''}`} id="quizFeedback">
                           {answered?.isCorrect ? (
                            <div className="feedback-icon correct">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="#fff" 
                                    strokeWidth="3" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        ) : (
                            <div className="feedback-icon wrong">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 24" 
                                    fill="#fff" 
                                    stroke="#fff" 
                                    strokeWidth="3" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </div>
                        )}
                            <div className="feedback-text">
                                <h4 id="feedbackTitle">{answered?.isCorrect ? 'Correct!' : 'Incorrect!'}</h4>
                                {correctAnswerText && <p>{correctAnswerText}</p>}
                            </div>
                        </div>
                        
                    </div>
                </div>
            </div>
        );
    });

    const abbr = firstScreen.abbreviation.map((item, index) => {
        return (
            <div className="step-item" key={index}>
                <div className="abbr">{item.abbr}</div>
                <div className="right-content">
                    <h4>{item.title}</h4>
                    <p>{item.short_description}</p>
                </div>
            </div>
        );
    });

    const introScreen =
        <div className={`skills ${props.Slug}`}>
            <div className="container">
                <Header type="primary" />
                <div className="content-wrap bg-gradient-light">
                    <div className="content-wrap-inner"> 
                    <div className="intro-box">
                        <div className="content">
                            {props.Content.Title && <h2>{props.Content.Title}</h2>}
                            {props.Content.sub_title && <h4>{props.Content.sub_title}</h4>}
                            <StrapiBlocksRenderer blocks={props.Content.description_main} />
                            
                            {abbr?<div className="steps">{abbr}</div>:""}
                            <button className={`button-right-arrow hover-bounce ${abbr?"":"mt---30"}`} onClick={handleStartSkill}>
                                Start {props.Content.Title} <span className="arrow-right"></span>
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>;

    const mainScreen =
        <div className={`quiz skills skills-carousel ${props.Slug}`}>
            <div className="container">
                <Header type="primary" />
                <div className="content-wrap bg-gradient-light">
                    <div className="content-wrap-inner">
                    <Carousel
                        Items={carouselItems}
                        onFinish={() => {
                            window.history.pushState({ screen: 'finish' }, '');
                            setShowFinish(true);
                        }}
                    />
                    </div>
                </div>
                <Footer />
            </div>
        </div>;

    const calculateScore = () => {
        const correctAnswers = Object.values(answers).filter(answer => answer.isCorrect).length;
        return correctAnswers;
    };

    const finishScreenView = (
        <FinishScreen
            title="Activity is Complete!"
            subtitle={`You've finished the ${props.Content.Title || 'Quiz'} Skill`}
            message="Great job! You've successfully completed all the questions."
            buttonText="Back to Activities"
            buttonLink="/ChooseOption"
            score={calculateScore()}
            totalQuestions={steps.length}
            showStats={false}
            className={props.Slug}
            showFavorite={true}
            isFavorited={isFavorited}
            onFavorite={handleFavorite}
            favoriteLoading={favoriteLoading}
        />
    );

    if (showFinish) return finishScreenView;
    return start ? mainScreen : introScreen;
}

export default Quiz;